import { useState, useEffect, useRef, useCallback } from 'react'
import { compressImage } from '../utils/imageCompression'
import { supabaseClient } from '../config/supabase'
import { ChatRoom, ChatMessage, ChatMessageSender, ChatReader, ChatMember } from '../types'
import toast from 'react-hot-toast'

export function useBatchChat() {
  const [rooms, setRooms] = useState<ChatRoom[]>([])
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [roomReaders, setRoomReaders] = useState<ChatReader[]>([])
  const [onlineMembers, setOnlineMembers] = useState<string[]>([])
  const [loadingRooms, setLoadingRooms] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)
  const [sending, setSending] = useState(false)
  const channelRef = useRef<ReturnType<typeof supabaseClient.channel> | null>(null)
  // Cached current user profile for optimistic updates
  const myProfileRef = useRef<{ id: string; sender: ChatMessageSender } | null>(null)
  // Track active room for polling effect
  const activeRoomIdRef = useRef<string | null>(null)

  // ─── Fetch and cache current user's profile ───────────────────────────────
  const loadMyProfile = useCallback(async () => {
    const { data: { user } } = await supabaseClient.auth.getUser()
    if (!user) return
    const { data } = await supabaseClient
      .from('alumni_profiles')
      .select('first_name, last_name, profile_picture')
      .eq('user_id', user.id)
      .single()
    myProfileRef.current = {
      id: user.id,
      sender: {
        first_name: data?.first_name || 'Me',
        last_name: data?.last_name || '',
        profile_picture: data?.profile_picture || null,
      },
    }
  }, [])

  // ─── Initialize: auto-join eligible rooms then fetch room list ────────────
  const initChat = useCallback(async () => {
    setLoadingRooms(true)
    try {
      await loadMyProfile()
      // Auto-join eligible rooms (creates rooms if needed)
      await supabaseClient.rpc('auto_join_eligible_rooms')
      // Fetch rooms
      await fetchRooms()
    } catch (err) {
      console.error('Failed to init chat:', err)
      toast.error('Failed to load chat rooms')
    } finally {
      setLoadingRooms(false)
    }
  }, [])

  const fetchRooms = useCallback(async () => {
    const { data, error } = await supabaseClient.rpc('get_my_chat_rooms')
    if (error) {
      console.error('Failed to fetch rooms:', error)
      return
    }
    setRooms(data || [])
  }, [])

  // ─── Select a room: fetch messages, mark read, subscribe to realtime ──────
  // ─── Fetch read receipts for the active room ─────────────────────────────
  const fetchReaders = useCallback(async (roomId: string) => {
    const { data, error } = await supabaseClient.rpc('get_room_read_receipts', { p_room_id: roomId })
    if (!error && data) setRoomReaders(data as ChatReader[])
  }, [])

  const selectRoom = useCallback(async (roomId: string) => {
    setActiveRoomId(roomId)
    setMessages([])
    setRoomReaders([])
    setHasMoreMessages(true)
    setLoadingMessages(true)

    try {
      // Fetch messages
      const { data, error } = await supabaseClient.rpc('get_chat_messages', {
        p_room_id: roomId,
        p_limit: 50,
      })
      if (error) throw error
      setMessages(data || [])
      setHasMoreMessages((data || []).length >= 50)

      // Mark room as read
      await supabaseClient.rpc('mark_room_read', { p_room_id: roomId })

      // Update unread count locally
      setRooms(prev =>
        prev.map(r => (r.id === roomId ? { ...r, unread_count: 0 } : r))
      )

      // Fetch who has read what
      await fetchReaders(roomId)
    } catch (err) {
      console.error('Failed to load messages:', err)
      toast.error('Failed to load messages')
    } finally {
      setLoadingMessages(false)
    }

    // Set up realtime subscription
    setupRealtimeChannel(roomId)
  }, [fetchReaders])

  const setupRealtimeChannel = useCallback((roomId: string) => {
    // Cleanup previous channel
    if (channelRef.current) {
      channelRef.current.unsubscribe()
      channelRef.current = null
    }

    // Use Broadcast instead of postgres_changes so messages are delivered
    // instantly to all subscribers without RLS evaluation on the Realtime server.
    const channel = supabaseClient
      .channel(`chat_room_${roomId}`)
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<{ alumni_id: string }>()
        const ids = Object.values(state).flat().map(p => p.alumni_id)
        setOnlineMembers(ids)
      })
      .on('presence', { event: 'join' }, ({ newPresences }) => {
        const ids = (newPresences as any[]).map((p) => p.alumni_id as string)
        setOnlineMembers(prev => [...new Set([...prev, ...ids])])
      })
      .on('presence', { event: 'leave' }, ({ leftPresences }) => {
        const ids = new Set((leftPresences as any[]).map((p) => p.alumni_id as string))
        setOnlineMembers(prev => prev.filter(id => !ids.has(id)))
      })
      .on('broadcast', { event: 'new_message' }, (payload) => {
        const msg = payload.payload.message as ChatMessage
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev
          return [...prev, msg]
        })
        // Immediately zero out the badge locally — don't wait for the DB
        setRooms(prev => prev.map(r => r.id === roomId ? { ...r, unread_count: 0 } : r))
        // Mark read in DB, then broadcast our read receipt + refresh room previews
        supabaseClient.rpc('mark_room_read', { p_room_id: roomId }).then(() => {
          if (myProfileRef.current) {
            channel.send({
              type: 'broadcast',
              event: 'room_read',
              payload: {
                alumni_id: myProfileRef.current.id,
                last_read_at: new Date().toISOString(),
                first_name: myProfileRef.current.sender.first_name,
                last_name: myProfileRef.current.sender.last_name,
                profile_picture: myProfileRef.current.sender.profile_picture,
              } satisfies ChatReader,
            })
          }
          // fetchRooms AFTER mark_room_read so the DB reflects the read state
          fetchRooms()
        })
      })
      .on('broadcast', { event: 'room_read' }, (payload) => {
        // A member just read the room — update their receipt instantly (no DB call)
        const reader = payload.payload as ChatReader
        setRoomReaders(prev => {
          const filtered = prev.filter(r => r.alumni_id !== reader.alumni_id)
          return [...filtered, reader]
        })
      })
      .on('broadcast', { event: 'chat_update' }, (payload) => {
        const update = payload.payload as {
          id: string
          content?: string
          edited_at?: string | null
          deleted_at?: string | null
        }
        setMessages(prev =>
          prev.map(m =>
            m.id === update.id
              ? {
                  ...m,
                  ...(update.content !== undefined ? { content: update.content } : {}),
                  ...(update.edited_at !== undefined ? { edited_at: update.edited_at } : {}),
                  ...(update.deleted_at !== undefined ? { deleted_at: update.deleted_at } : {}),
                }
              : m
          )
        )
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED' && myProfileRef.current) {
          await channel.track({ alumni_id: myProfileRef.current.id })
        }
      })

    channelRef.current = channel
  }, [fetchRooms])

  // ─── Send message ─────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (content: string, replyToId?: string, replyToContent?: string | null, replyToSender?: string | null) => {
    if (!activeRoomId || sending) return
    setSending(true)

    // Optimistic update — add the message to the list immediately
    const optimisticId = `optimistic-${Date.now()}`
    const optimisticMsg: ChatMessage = {
      id: optimisticId,
      room_id: activeRoomId,
      sender_id: myProfileRef.current?.id || '',
      content,
      reply_to_id: replyToId || null,
      sent_at: new Date().toISOString(),
      edited_at: null,
      deleted_at: null,
      sender: myProfileRef.current?.sender || { first_name: 'Me', last_name: '', profile_picture: null },
      reply_to_content: replyToContent || null,
      reply_to_sender: replyToSender || null,
    }
    setMessages(prev => [...prev, optimisticMsg])

    try {
      const { data, error } = await supabaseClient.rpc('send_chat_message', {
        p_room_id: activeRoomId,
        p_content: content,
        p_reply_to_id: replyToId || null,
      })
      if (error) throw error
      if (data && !data.success) {
        // Rollback optimistic message on failure
        setMessages(prev => prev.filter(m => m.id !== optimisticId))
        toast.error(data.error || 'Failed to send message')
      } else {
        // Replace optimistic message with the real DB id
        const realId = data?.message_id
        if (realId) {
          const realMsg: ChatMessage = { ...optimisticMsg, id: realId }
          setMessages(prev =>
            prev.map(m => m.id === optimisticId ? realMsg : m)
          )
          // Broadcast to all other room members via Supabase Broadcast
          channelRef.current?.send({
            type: 'broadcast',
            event: 'new_message',
            payload: { message: realMsg },
          })
          // Update room list preview immediately for the sender
          fetchRooms()
        }
      }
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== optimisticId))
      console.error('Failed to send message:', err)
      toast.error('Failed to send message')
    } finally {
      setSending(false)
    }
  }, [activeRoomId, sending, fetchRooms])

  // ─── Edit message ─────────────────────────────────────────────────────────
  const editMessage = useCallback(async (messageId: string, content: string) => {
    const editedAt = new Date().toISOString()
    // Optimistic update for sender
    setMessages(prev =>
      prev.map(m => m.id === messageId ? { ...m, content, edited_at: editedAt } : m)
    )
    try {
      const { data, error } = await supabaseClient.rpc('edit_chat_message', {
        p_message_id: messageId,
        p_content: content,
      })
      if (error) throw error
      if (data && !data.success) {
        toast.error(data.error || 'Failed to edit message')
      } else {
        // Broadcast edit to other room members
        channelRef.current?.send({
          type: 'broadcast',
          event: 'chat_update',
          payload: { id: messageId, content, edited_at: editedAt, deleted_at: null },
        })
      }
    } catch (err) {
      console.error('Failed to edit message:', err)
      toast.error('Failed to edit message')
    }
  }, [])

  // ─── Delete message ───────────────────────────────────────────────────────
  const deleteMessage = useCallback(async (messageId: string) => {
    const deletedAt = new Date().toISOString()
    // Optimistic update for sender
    setMessages(prev =>
      prev.map(m => m.id === messageId ? { ...m, deleted_at: deletedAt } : m)
    )
    try {
      const { data, error } = await supabaseClient.rpc('delete_chat_message', {
        p_message_id: messageId,
      })
      if (error) throw error
      if (data && !data.success) {
        toast.error(data.error || 'Failed to delete message')
        // Rollback
        setMessages(prev =>
          prev.map(m => m.id === messageId ? { ...m, deleted_at: null } : m)
        )
      } else {
        // Broadcast delete to other room members
        channelRef.current?.send({
          type: 'broadcast',
          event: 'chat_update',
          payload: { id: messageId, deleted_at: deletedAt },
        })
      }
    } catch (err) {
      console.error('Failed to delete message:', err)
      toast.error('Failed to delete message')
      setMessages(prev =>
        prev.map(m => m.id === messageId ? { ...m, deleted_at: null } : m)
      )
    }
  }, [])

  // ─── Load more (older) messages ───────────────────────────────────────────
  const loadMoreMessages = useCallback(async () => {
    if (!activeRoomId || !hasMoreMessages || loadingMessages) return
    setLoadingMessages(true)
    try {
      const oldest = messages[0]?.sent_at || null
      const { data, error } = await supabaseClient.rpc('get_chat_messages', {
        p_room_id: activeRoomId,
        p_limit: 50,
        p_before: oldest,
      })
      if (error) throw error
      const olderMessages: ChatMessage[] = data || []
      setHasMoreMessages(olderMessages.length >= 50)
      setMessages(prev => [...olderMessages, ...prev])
    } catch (err) {
      console.error('Failed to load more messages:', err)
    } finally {
      setLoadingMessages(false)
    }
  }, [activeRoomId, hasMoreMessages, loadingMessages, messages])

  // ─── Leave room ───────────────────────────────────────────────────────────
  const leaveRoom = useCallback(async (roomId: string) => {
    try {
      const { data, error } = await supabaseClient.rpc('leave_chat_room', {
        p_room_id: roomId,
      })
      if (error) throw error
      if (data?.success) {
        toast.success('Left the room')
        setRooms(prev => prev.filter(r => r.id !== roomId))
        if (activeRoomId === roomId) {
          setActiveRoomId(null)
          setMessages([])
        }
      } else {
        toast.error(data?.error || 'Failed to leave room')
      }
    } catch (err) {
      console.error('Failed to leave room:', err)
      toast.error('Failed to leave room')
    }
  }, [activeRoomId])

  // ─── Upload image and send as message ─────────────────────────────────────
  const uploadAndSendImage = useCallback(async (file: File) => {
    if (!activeRoomId) return
    const compressed = await compressImage(file, { maxWidth: 1200, maxHeight: 1200, quality: 0.80 })
    const path = `${activeRoomId}/${Date.now()}-${Math.random().toString(36).slice(2)}.jpg`
    const { error: uploadError } = await supabaseClient.storage
      .from('chat-images')
      .upload(path, compressed, { contentType: 'image/jpeg' })
    if (uploadError) {
      toast.error('Failed to upload image')
      return
    }
    const { data: urlData } = supabaseClient.storage
      .from('chat-images')
      .getPublicUrl(path)
    await sendMessage(urlData.publicUrl)
  }, [activeRoomId, sendMessage])

  // ─── Toggle mute ─────────────────────────────────────────────────────────
  const toggleMute = useCallback(async (roomId: string) => {
    try {
      const { data, error } = await supabaseClient.rpc('toggle_room_mute', {
        p_room_id: roomId,
      })
      if (error) throw error
      if (data?.success) {
        const muted: boolean = data.is_muted
        setRooms(prev =>
          prev.map(r => r.id === roomId ? { ...r, is_muted: muted, unread_count: muted ? 0 : r.unread_count } : r)
        )
        toast.success(muted ? 'Room muted — notifications suppressed' : 'Room unmuted')
      } else {
        toast.error(data?.error || 'Failed to toggle mute')
      }
    } catch (err) {
      console.error('Failed to toggle mute:', err)
      toast.error('Failed to toggle mute')
    }
  }, [])

  // ─── Cleanup on unmount ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        channelRef.current.untrack()
        channelRef.current.unsubscribe()
        channelRef.current = null
      }
    }
  }, [])

  // ─── Keep ref in sync with state (for polling effect) ────────────────────
  useEffect(() => {
    activeRoomIdRef.current = activeRoomId
  }, [activeRoomId])

  // ─── Room list polling: refresh previews every 5 s unconditionally ────────
  // This runs regardless of which room is selected, so the sidebar preview
  // (last message, sender, timestamp) always stays current.
  useEffect(() => {
    const interval = setInterval(fetchRooms, 5000)
    return () => clearInterval(interval)
  }, [fetchRooms])

  // ─── Polling fallback: fetch latest messages every 3 s ───────────────────
  // Broadcast is the fast path; this guarantees messages appear even if the
  // Realtime WebSocket connection fails or the channel authorization silently drops.
  useEffect(() => {
    if (!activeRoomId) return
    const interval = setInterval(async () => {
      const roomId = activeRoomIdRef.current
      if (!roomId) return
      const { data, error } = await supabaseClient.rpc('get_chat_messages', {
        p_room_id: roomId,
        p_limit: 20,
      })
      if (error || !data) return
      const polled: ChatMessage[] = data || []
      let foundFresh = false
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id))
        const fresh = polled.filter(m => !existingIds.has(m.id))
        if (fresh.length === 0) return prev
        foundFresh = true
        return [...prev, ...fresh]
      })
      // Refresh room list preview whenever new messages are found
      if (foundFresh) fetchRooms()
      // Refresh read receipts on every poll tick
      fetchReaders(roomId)
    }, 3000)
    return () => clearInterval(interval)
  }, [activeRoomId, fetchRooms, fetchReaders])

  const activeRoom = rooms.find(r => r.id === activeRoomId) || null

  async function fetchRoomMembers(roomId: string): Promise<ChatMember[]> {
    const { data, error } = await supabaseClient.rpc('get_room_members', { p_room_id: roomId })
    if (error) return []
    return (data as ChatMember[]) ?? []
  }

  return {
    rooms,
    activeRoom,
    activeRoomId,
    messages,
    roomReaders,
    onlineMembers,
    loadingRooms,
    loadingMessages,
    hasMoreMessages,
    sending,
    initChat,
    selectRoom,
    sendMessage,
    editMessage,
    deleteMessage,
    loadMoreMessages,
    leaveRoom,
    toggleMute,
    uploadAndSendImage,
    fetchRooms,
    fetchRoomMembers,
  }
}
